import { useEffect, useMemo, useState, useContext } from 'react';
import { Button, Col, Form, Input, InputNumber, Modal, Popconfirm, Row, Space, Switch, Table, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { del, get, post, put } from '../../api/base';
import { RootState } from '../../store/store';
import { NotificationContext } from '../../providers/NotificationProvider';
import { useTranslation } from 'react-i18next';
import { VirtualCourseInterestOptionType } from '../../types/types';

type MethodologyAdminType = {
  id: number;
  name: string;
};

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { access, rol, user } = useSelector((state: RootState) => state.user);
  const { triggerNotification } = useContext(NotificationContext);
  const [courseForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [methodologies, setMethodologies] = useState<MethodologyAdminType[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [courseSaving, setCourseSaving] = useState(false);
  const [editingCourse, setEditingCourse] = useState<VirtualCourseInterestOptionType | null>(null);
  const [courseOptions, setCourseOptions] = useState<VirtualCourseInterestOptionType[]>([]);

  const isSettingsAdmin = rol === 'Administrador' || user?.is_staff;

  const headers = useMemo(() => {
    return access ? { Authorization: `Bearer ${access}` } : undefined;
  }, [access]);

  const loadMethodologies = () => {
    setLoading(true);
    get('methodologies/', { headers })
      .then((response: any) => {
        setMethodologies(response?.data ?? []);
      })
      .catch(() => {
        triggerNotification('error', t('Error'), t('Error al cargar las metodologías'), 'bottomRight');
      })
      .finally(() => setLoading(false));
  };

  const loadCourseOptions = () => {
    setCoursesLoading(true);
    get('settings/virtual-course-interest-options/', { headers })
      .then((response: any) => {
        setCourseOptions(response?.data ?? []);
      })
      .catch((error: any) => {
        const message = error?.response?.data?.message || t('Error al cargar los cursos virtuales');
        triggerNotification('error', t('Error'), message, 'bottomRight');
      })
      .finally(() => setCoursesLoading(false));
  };

  useEffect(() => {
    if (!isSettingsAdmin) {
      navigate('/proyectos');
      return;
    }
    loadMethodologies();
    loadCourseOptions();
    // eslint-disable-next-line
  }, [isSettingsAdmin]);

  const handleDelete = (record: MethodologyAdminType) => {
    if (record.id === 1) {
      triggerNotification('error', t('Error'), t('No se puede eliminar HAZOP'), 'bottomRight');
      return;
    }
    del(`methodologies/${record.id}/`, { headers })
      .then(() => {
        triggerNotification('success', t('Éxito'), t('Metodología eliminada'), 'bottomRight');
        loadMethodologies();
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.detail ||
          t('No se pudo eliminar la metodología');
        triggerNotification('error', t('Error'), message, 'bottomRight');
      });
  };

  const openCreateCourseModal = () => {
    setEditingCourse(null);
    courseForm.setFieldsValue({
      name: '',
      sort_order: courseOptions.length + 1,
      is_active: true,
    });
    setCourseModalOpen(true);
  };

  const openEditCourseModal = (record: VirtualCourseInterestOptionType) => {
    setEditingCourse(record);
    courseForm.setFieldsValue({
      name: record.name,
      sort_order: record.sort_order,
      is_active: record.is_active,
    });
    setCourseModalOpen(true);
  };

  const handleCloseCourseModal = () => {
    setCourseModalOpen(false);
    setEditingCourse(null);
    courseForm.resetFields();
  };

  const handleSubmitCourse = async () => {
    try {
      const values = await courseForm.validateFields();
      setCourseSaving(true);

      if (editingCourse) {
        await put(`settings/virtual-course-interest-options/${editingCourse.id}/`, values, { headers });
        triggerNotification('success', t('Éxito'), t('Curso virtual actualizado'), 'bottomRight');
      } else {
        await post('settings/virtual-course-interest-options/', values, { headers });
        triggerNotification('success', t('Éxito'), t('Curso virtual creado'), 'bottomRight');
      }

      handleCloseCourseModal();
      loadCourseOptions();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        t('No se pudo guardar el curso virtual');
      triggerNotification('error', t('Error'), message, 'bottomRight');
    } finally {
      setCourseSaving(false);
    }
  };

  const handleDeleteCourse = (record: VirtualCourseInterestOptionType) => {
    del(`settings/virtual-course-interest-options/${record.id}/`, { headers })
      .then(() => {
        triggerNotification('success', t('Éxito'), t('Curso virtual eliminado'), 'bottomRight');
        loadCourseOptions();
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          t('No se pudo eliminar el curso virtual');
        triggerNotification('error', t('Error'), message, 'bottomRight');
      });
  };

  const columns = [
    {
      title: t('Nombre'),
      dataIndex: 'name',
      key: 'name',
      width: 400,
    },
    {
      title: t('Acciones'),
      key: 'actions',
      width: 90,
      align: 'center' as const,
      render: (_: any, record: MethodologyAdminType) => (
        <Space size='small'>
          <Button
            type='text'
            onClick={() => navigate(`/configuraciones/metodologias/${record.id}`)}
            aria-label={t('Editar')}
            icon={<EditOutlined />}
          />
          {record.id === 1 ? (
            <Button
              type='text'
              danger
              disabled
              aria-label={t('Eliminar')}
              icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
              style={{ color: '#ff4d4f' }}
            />
          ) : (
            <Popconfirm
              title={t('Eliminar metodología')}
              description={t('¿Estás seguro de eliminar esta metodología?')}
              okText={t('Eliminar')}
              cancelText={t('Cancelar')}
              onConfirm={() => handleDelete(record)}
            >
              <Button
                type='text'
                danger
                aria-label={t('Eliminar')}
                icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                style={{ color: '#ff4d4f' }}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const courseColumns = [
    {
      title: t('Nombre'),
      dataIndex: 'name',
      key: 'name',
      width: 320,
    },
    {
      title: t('Orden'),
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 100,
    },
    {
      title: t('Activo'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (value: boolean) => (value ? t('Sí') : t('No')),
    },
    {
      title: t('Acciones'),
      key: 'actions',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: VirtualCourseInterestOptionType) => (
        <Space size='small'>
          <Button
            type='text'
            onClick={() => openEditCourseModal(record)}
            aria-label={t('Editar')}
            icon={<EditOutlined />}
          />
          <Popconfirm
            title={t('Eliminar curso virtual')}
            description={t('¿Estás seguro de eliminar este curso virtual?')}
            okText={t('Eliminar')}
            cancelText={t('Cancelar')}
            onConfirm={() => handleDeleteCourse(record)}
          >
            <Button
              type='text'
              danger
              aria-label={t('Eliminar')}
              icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
              style={{ color: '#ff4d4f' }}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Row className='content' style={{ margin: 24 }}>
      <Col span={24}>
        <div className='title-full'>
          <Typography.Title level={2} style={{ margin: 0 }}>
            {t('Configuraciones')}
          </Typography.Title>
          <Button type='primary' onClick={() => navigate('/configuraciones/metodologias/nueva')}>
            <PlusOutlined /> {t('Nueva Metodología')}
          </Button>
        </div>
        <div className='ant-divider' />

        <Typography.Title level={4}>{t('Metodologías')}</Typography.Title>
        <div style={{ maxWidth: 720 }}>
          <Table
            rowKey='id'
            loading={loading}
            dataSource={methodologies}
            columns={columns}
            pagination={false}
          />
        </div>

        <div style={{ marginTop: 32 }}>
          <div className='title-full'>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {t('Cursos Virtuales de Interés')}
            </Typography.Title>
            <Button type='primary' onClick={openCreateCourseModal}>
              <PlusOutlined /> {t('Nuevo Curso Virtual')}
            </Button>
          </div>
          <div style={{ maxWidth: 720, marginTop: 16 }}>
            <Table
              rowKey='id'
              loading={coursesLoading}
              dataSource={courseOptions}
              columns={courseColumns}
              pagination={false}
            />
          </div>
        </div>
      </Col>

      <Modal
        title={editingCourse ? t('Editar Curso Virtual') : t('Nuevo Curso Virtual')}
        open={courseModalOpen}
        onCancel={handleCloseCourseModal}
        onOk={handleSubmitCourse}
        okText={editingCourse ? t('Guardar') : t('Crear')}
        cancelText={t('Cancelar')}
        confirmLoading={courseSaving}
        destroyOnClose
      >
        <Form form={courseForm} layout='vertical'>
          <Form.Item
            label={t('Nombre')}
            name='name'
            rules={[{ required: true, message: t('Por favor ingresa el nombre del curso virtual.') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('Orden')}
            name='sort_order'
            rules={[{ required: true, message: t('Por favor ingresa el orden del curso virtual.') }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label={t('Activo')} name='is_active' valuePropName='checked'>
            <Switch checkedChildren={t('Sí')} unCheckedChildren={t('No')} />
          </Form.Item>
        </Form>
      </Modal>
    </Row>
  );
};

export default Settings;
